import React, { useEffect, useState, useRef } from "react";
import { FaCamera } from "react-icons/fa";
import { useTranslation } from "react-i18next";

const fieldStyle = (isMobile) => ({
  width: "100%",
  borderRadius: "10px",
  border: "1px solid rgba(201,168,75,0.4)",
  background: "#0A031E",
  color: "#fff2d2",
  padding: isMobile ? "12px" : "14px",
  outline: "none",
});

const readOnlyFieldStyle = (isMobile) => ({
  ...fieldStyle(isMobile),
  background: "rgba(255,255,255,0.05)",
  color: "#888",
  cursor: "not-allowed",
  border: "1px solid rgba(201,168,75,0.15)",
});

const labelStyle = {
  marginBottom: "6px",
  color: "#fff2d2",
  fontSize: "13px",
};

const PersonalProfile = ({ onBack }) => {
  const { t } = useTranslation();
  const isMobile = typeof window !== "undefined" ? window.innerWidth <= 768 : false;
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [nationality, setNationality] = useState("");
  const [email, setEmail] = useState("");
  const [mobile, setMobile] = useState("");
  const [dob, setDob] = useState("");
  const [cdob, setCdob] = useState("");
  const [gender, setGender] = useState("");
  const [age, setAge] = useState("");
  const [phoneError, setPhoneError] = useState("");
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saveMsg, setSaveMsg] = useState(null); // { type: 'success'|'error', text: string }
  const [uploading, setUploading] = useState(false);
  const [profileImage, setProfileImage] = useState(null);
  const fileRef = useRef(null);
  const cameraRef = useRef(null);

  useEffect(() => {
    const userName = localStorage.getItem("username");
    const userID = localStorage.getItem("userID");
    const langId = localStorage.getItem("languageId") || "2224";

    const fetchProfile = async () => {
      try {
        const response = await fetch(
          "https://xianhefairy.com/LotusFairy_API/api/User/GetProfile",
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              UserName: userName,
              ID: userID,
              LangId: parseInt(langId),
              UserToken: "",
              Token: {
                AccessToken: "xAfhn90xflfHfd.fjldfj@fdhLXHCHchlhsdflsdfhawWFRwzxxp",
              },
            }),
          }
        );
        const result = await response.json();
        if (result?.Profiles?.[0]) {
          const p = result.Profiles[0];
          setFirstName(p.FirstName || "");
          setLastName(p.LastName || "");
          setNationality(p.Country || "");
          setEmail(p.Email || "");
          setMobile(p.MobileNo || "");
          setDob(p.DOB || "");
          setCdob(p.CDOB || "");
          setGender(p.Gender || "");
          setAge(p.AGE ?? "");
        }
        if (userID) fetchProfileImage(userID);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, []);

  const fetchProfileImage = async (id) => {
    try {
      const url = `https://xianhefairy.com/LotusFairy_API/api/member/GetProfileImage?ID=${id}&t=${Date.now()}`;
      setProfileImage(url);
    } catch (error) {
      console.error('Error fetching profile image:', error);
    }
  };

  const handleImageUpload = async (file) => {
    const userID = localStorage.getItem("userID");
    if (!userID) {
      alert(t("account.userIdNotFound", { defaultValue: "User ID not found. Please re-login." }));
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('profile', file, `profile_${userID}.jpg`);

    try {
      const response = await fetch(
        `https://xianhefairy.com/LotusFairy_API/api/member/PostProfileImageUpload?ID=${userID}`,
        {
          method: 'POST',
          body: formData,
        }
      );

      if (response.ok) {
        fetchProfileImage(userID);
      } else {
        alert(t("account.failedToUpload", { defaultValue: "Failed to upload the profile image." }));
      }
    } catch (error) {
      console.error('Error uploading image:', error);
      alert(t("account.errorUploading", { defaultValue: "An error occurred while uploading." }));
    } finally {
      setUploading(false);
    }
  };

  const requestCameraAccess = async () => {
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) return false;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true });
      stream.getTracks().forEach((t) => t.stop());
      return true;
    } catch {
      return false;
    }
  };

  const openCamera = async () => {
    const hasPermission = await requestCameraAccess();
    if (!hasPermission) {
      alert(t("account.cameraPermissionRequired", { defaultValue: "Camera permission is required." }));
      return;
    }
    if (cameraRef.current) cameraRef.current.click();
  };

  const openGallery = () => {
    if (fileRef.current) fileRef.current.click();
  };

  const handleFileChange = (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    handleImageUpload(file);
  };

  const handleCameraChange = (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    handleImageUpload(file);
  };

  const phoneRegex = /^\+?[\d\s\-().]{7,20}$/;

  const validatePhone = (value) => {
    if (!value.trim()) return "";
    return phoneRegex.test(value.trim())
      ? ""
      : t("account.invalidPhone", { defaultValue: "Please enter a valid phone number (7–20 digits, may start with +)." });
  };

  const handleMobileChange = (e) => {
    setMobile(e.target.value);
    if (phoneError) setPhoneError(validatePhone(e.target.value));
  };

  const handleMobileBlur = () => {
    setPhoneError(validatePhone(mobile));
  };

  const handleSave = async () => {
    setSaveMsg(null);

    const error = validatePhone(mobile);
    if (error) {
      setPhoneError(error);
      return;
    }

    setSaving(true);
    try {
      const userName = localStorage.getItem("username");
      const userID = localStorage.getItem("userID");
      const langId = localStorage.getItem("languageId") || "2224";

      const response = await fetch(
        "https://xianhefairy.com/LotusFairy_API/api/user/ProfileSaveUpdate?token=xAfhn90xflfHfd.fjldfj@fdhLXHCHchlhsdflsdfhawWFRwzxxp",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            ID: userID,
            FName: firstName,
            LName: lastName,
            Country: nationality,
            MobileNo: mobile,
            Email: email,
            DateOfBirth: dob,
            ChineseDateOfBirth: cdob,
            Gender: gender,
            Age: age,
            Password: "",
            RefNo: "",
            Search_Interest: "",
          }),
        }
      );

      const result = await response.json();
      if (response.ok && result) {
        localStorage.setItem("profile", JSON.stringify({ firstName, lastName, nationality, email, mobile }));
        setSaveMsg({ type: "success", text: t("account.saveSuccess", { defaultValue: "Profile updated successfully." }) });
      } else {
        setSaveMsg({ type: "error", text: result?.Message || t("account.saveFailed", { defaultValue: "Failed to update profile. Please try again." }) });
      }
    } catch (e) {
      console.error(e);
      setSaveMsg({ type: "error", text: t("account.saveFailed", { defaultValue: "Failed to update profile. Please try again." }) });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div style={{ minHeight: "100vh", display: "grid", placeItems: "center", color: "#deaf77", fontFamily: "DMSans, sans-serif" }}>
        {t("account.loading", { defaultValue: "Loading..." })}
      </div>
    );
  }

  return (
    <div
      style={{
        minHeight: "100vh",
        padding: isMobile ? "20px 14px 120px" : "34px 20px 160px",
        fontFamily: "DMSans, sans-serif",
      }}
    >
      <div
        style={{
          width: "100%",
          maxWidth: "680px",
          margin: "0 auto",
          borderRadius: "18px",
          border: "2px solid rgba(201,168,75,0.4)",
          background: "#0A031E",
          boxShadow: "0 12px 30px rgba(0,0,0,0.45)",
          padding: isMobile ? "18px" : "24px",
          color: "#fff2d2",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: 12 }}>
          <button
            onClick={() => (onBack ? onBack() : window.history.back())}
            aria-label={t("account.back", { defaultValue: "Back" })}
            style={{
              width: "34px",
              height: "34px",
              borderRadius: "50%",
              border: "1px solid rgba(201,168,75,0.5)",
              background: "transparent",
              color: "#deaf77",
              display: "grid",
              placeItems: "center",
              cursor: "pointer",
            }}
          >
            &#8592;
          </button>

          <h1 style={{ margin: 0, color: "#deaf77", fontFamily: "DMSans-Bold, DMSans, sans-serif", fontSize: isMobile ? "22px" : "26px" }}>
            {t("account.personalProfile", { defaultValue: "Personal Profile" })}
          </h1>
        </div>

        <div style={{ display: "grid", gap: "12px" }}>
          <div style={{ display: "flex", gap: "14px", alignItems: "center" }}>
            <div
              style={{
                width: 74,
                height: 74,
                borderRadius: "50%",
                background: profileImage ? `url(${profileImage}) center/cover` : "rgba(255,255,255,0.04)",
                border: "1px solid rgba(201,168,75,0.4)",
                display: "grid",
                placeItems: "center",
                color: "#deaf77",
                overflow: "hidden",
              }}
            />

            <div style={{ display: "grid", gap: 8 }}>
              <input ref={fileRef} type="file" accept="image/*" onChange={handleFileChange} style={{ display: "none" }} />
              <input ref={cameraRef} type="file" accept="image/*" capture="environment" onChange={handleCameraChange} style={{ display: "none" }} />
              <div style={{ display: "flex", gap: 8 }}>
                <button
                  onClick={openGallery}
                  disabled={uploading}
                  style={{
                    borderRadius: 8,
                    border: "1px solid rgba(201,168,75,0.4)",
                    background: "transparent",
                    color: "#deaf77",
                    padding: "8px 12px",
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    cursor: uploading ? "not-allowed" : "pointer",
                  }}
                >
                  <FaCamera /> {uploading ? t("account.uploading", { defaultValue: "Uploading..." }) : t("account.gallery", { defaultValue: "Gallery" })}
                </button>
                <button
                  onClick={openCamera}
                  disabled={uploading}
                  style={{
                    borderRadius: 8,
                    border: "1px solid rgba(201,168,75,0.4)",
                    background: "transparent",
                    color: "#deaf77",
                    padding: "8px 12px",
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    cursor: uploading ? "not-allowed" : "pointer",
                  }}
                >
                  <FaCamera /> {t("account.camera", { defaultValue: "Camera" })}
                </button>
              </div>
              {/* <div style={{ fontSize: 12, color: "#D8CCA8" }}>PNG, JPG up to 2MB</div> */}
            </div>
          </div>

          <div style={{ display: "grid", gap: 10 }}>
            <div>
              <div style={labelStyle}>{t("account.firstName", { defaultValue: "First Name" })}</div>
              <input value={firstName} onChange={(e) => setFirstName(e.target.value)} style={fieldStyle(isMobile)} />
            </div>

            <div>
              <div style={labelStyle}>{t("account.lastName", { defaultValue: "Last Name" })}</div>
              <input value={lastName} onChange={(e) => setLastName(e.target.value)} style={fieldStyle(isMobile)} />
            </div>

            <div>
              <div style={labelStyle}>{t("account.nationality", { defaultValue: "Nationality" })}</div>
              <input value={nationality} onChange={(e) => setNationality(e.target.value)} style={fieldStyle(isMobile)} />
            </div>

            <div>
              <div style={labelStyle}>{t("account.email", { defaultValue: "Email" })}</div>
              <input value={email} readOnly style={readOnlyFieldStyle(isMobile)} />
            </div>

            <div>
              <div style={labelStyle}>{t("account.mobile", { defaultValue: "Mobile" })}</div>
              <input
                value={mobile}
                onChange={handleMobileChange}
                onBlur={handleMobileBlur}
                style={{
                  ...fieldStyle(isMobile),
                  ...(phoneError ? { border: "1px solid #FF665E" } : {}),
                }}
              />
              {phoneError && (
                <div style={{ marginTop: "4px", fontSize: "12px", color: "#FF665E" }}>
                  {phoneError}
                </div>
              )}
            </div>
          </div>

          <button
            onClick={handleSave}
            disabled={saving}
            style={{
              marginTop: "6px",
              width: "100%",
              borderRadius: "999px",
              border: "1px solid rgba(201,168,75,0.4)",
              background: saving ? "rgba(201,168,75,0.12)" : "linear-gradient(90deg, rgba(46,22,40,0.6), rgba(103,62,84,0.6))",
              color: "#deaf77",
              padding: "14px",
              cursor: saving ? "not-allowed" : "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "8px",
              fontFamily: "DMSans-Bold, DMSans, sans-serif",
              fontSize: "16px",
            }}
          >
            {saving ? t("account.saving", { defaultValue: "Saving..." }) : t("account.save", { defaultValue: "Save" })}
          </button>

          {saveMsg && (
            <p style={{ margin: "8px 0 0", fontSize: "13px", textAlign: "center", color: saveMsg.type === "success" ? "#4caf50" : "#FF665E" }}>
              {saveMsg.text}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default PersonalProfile;
